import * as React from 'react';

/** The browser's fix in the shape `POST /attendance/punch` takes. */
export interface Fix {
  lat: number;
  lng: number;
  accuracy: number;
}

/** Why there is no fix: refused, no signal, too slow, or no Geolocation API at all. */
export type NoFixReason = 'denied' | 'unavailable' | 'timeout' | 'unsupported';

export type FixResult = { fix: Fix } | { fix: null; reason: NoFixReason };

/** Long enough for a phone's GPS to settle indoors; the punch is saved without a fix after. */
export const FIX_TIMEOUT_MS = 15_000;

/**
 * One fresh fix, read only at the punch (FR-ATT-3, ADR-056): high accuracy, no cached position.
 * Never rejects — without a fix the punch is still sent (and flagged by the API when required).
 */
export function readFix(
  geolocation: Geolocation | undefined = typeof navigator === 'undefined'
    ? undefined
    : navigator.geolocation,
): Promise<FixResult> {
  if (!geolocation) return Promise.resolve({ fix: null, reason: 'unsupported' });
  return new Promise((resolve) => {
    geolocation.getCurrentPosition(
      ({ coords }) => {
        resolve({
          fix: { lat: coords.latitude, lng: coords.longitude, accuracy: coords.accuracy },
        });
      },
      (error) => {
        resolve({
          fix: null,
          reason:
            error.code === error.PERMISSION_DENIED
              ? 'denied'
              : error.code === error.TIMEOUT
                ? 'timeout'
                : 'unavailable',
        });
      },
      { enableHighAccuracy: true, timeout: FIX_TIMEOUT_MS, maximumAge: 0 },
    );
  });
}

export type LocationPermission = PermissionState | 'unknown';

/**
 * The site's location permission, kept current while mounted (`unknown` where the Permissions
 * API is missing, e.g. older iOS Safari) — lets the today card explain before the prompt and say
 * how to unblock it.
 */
export function useLocationPermission(): LocationPermission {
  const [state, setState] = React.useState<LocationPermission>('unknown');
  React.useEffect(() => {
    let status: PermissionStatus | null = null;
    let active = true;
    const update = () => {
      if (active && status) setState(status.state);
    };
    if (typeof navigator !== 'undefined' && 'permissions' in navigator) {
      navigator.permissions
        .query({ name: 'geolocation' })
        .then((s) => {
          status = s;
          update();
          s.addEventListener('change', update);
        })
        .catch(() => {
          // Not queryable here: stay `unknown`; the prompt still appears at the punch.
        });
    }
    return () => {
      active = false;
      status?.removeEventListener('change', update);
    };
  }, []);
  return state;
}
